import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const CHECKBOX_COL_WIDTH = 70;
const ID_COL_WIDTH = 170;
const FIELD_COL_WIDTH = 180;
const PROPERTY_ADDRESS_COL_WIDTH = 300;
const HOVER_BACKGROUND = "#DCEAF8";

const formatCreatedDate = (dateValue) => {
  if (!dateValue) return "—";

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

const FIELD_COLUMNS = [
  {
    label: "Homeowner Name",
    matchKey: "homeowner_name",
    render: (app) => app.homeowner_name,
  },
  {
    label: "Property Address",
    matchKey: "property_address",
    render: (app) =>
      [app.street_address, app.city, app.state_name]
        .filter(Boolean)
        .join(", ") + (app.zip_code ? ` ${app.zip_code}` : ""),
  },
  {
    label: "Originator Name",
    matchKey: null,
    render: (app) => app.originator_name || "—",
  },
  {
    label: "Created Date",
    matchKey: null,
    render: (app) => formatCreatedDate(app.created_date),
  },
  {
    label: "Loan Officer",
    matchKey: "loan_officer_name",
    render: (app) =>
      app.loan_officer_name && app.loan_officer_name.trim()
        ? app.loan_officer_name
        : "-",
  },
  {
    label: "Status",
    matchKey: null,
    render: (app) => app.application_status,
  },
];

const TABLE_WIDTH =
  CHECKBOX_COL_WIDTH +
  ID_COL_WIDTH +
  FIELD_COL_WIDTH * (FIELD_COLUMNS.length - 1) +
  PROPERTY_ADDRESS_COL_WIDTH;

const rowBg = (index) => (index % 2 === 0 ? "#E9E9E9" : "#FFFFFF");

export default function DuplicateApplicationsModal({
  open,
  onClose,
  group,
  onMarkDuplicate,
  loading = false,
  error = null,
}) {
  const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const applications = group?.applications || [];

  const lockedIds = useMemo(
    () =>
      new Set(
        applications
          .filter((app) => app.is_duplicate)
          .map((app) => app.application_number),
      ),
    [applications],
  );

  useEffect(() => {
    setSelectedIds(new Set(lockedIds));
  }, [group, open]);

  if (!group) return null;

  const toggleSelect = (id) => {
    if (lockedIds.has(id)) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else if (next.size < Math.max(applications.length - 1, 0)) {
        next.add(id);
      }
      return next;
    });
  };

  const newSelections = Array.from(selectedIds).filter(
    (id) => !lockedIds.has(id),
  );

  const handleMarkDuplicates = () => {
    onMarkDuplicate?.(newSelections, group);
  };

  const isAnySelected = newSelections.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 950,
          maxWidth: "95vw",
          maxHeight: "95vh",
          borderRadius: "8px",
          p: 0,
          overflowY: "auto",
        },
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            pt: 3,
            pb: 2,
          }}
        >
          <Typography
            sx={{
              fontFamily: "'proxima-regular', sans-serif",
              fontWeight: 600,
              fontSize: 16,
              lineHeight: "26px",
              color: "#000000",
            }}
          >
            {group.group_name}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ width: 24, height: 24, p: 0 }}
          >
            <CloseIcon sx={{ width: 24, height: 24, color: "#000000" }} />
          </IconButton>
        </Box>

        {/* Loading state */}
        {loading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              py: 6,
              mx: 2,
              mb: 2,
            }}
          >
            <CircularProgress size={28} sx={{ color: "#077EE0" }} />
          </Box>
        )}

        {/* Error state */}
        {!loading && error && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "4px",
              py: 6,
              mx: 2,
              mb: 2,
            }}
          >
            <Typography
              sx={{
                fontFamily: "'proxima-regular', sans-serif",
                fontSize: 14,
                color: "#D32F2F",
                textAlign: "center",
              }}
            >
              {error}
            </Typography>
          </Box>
        )}

        {/* Table Frame */}
        {!loading && !error && (
          <Box
            sx={{
              mx: 2,
              mb: 2,
              border: "1px solid #000000",
              borderRadius: 0,
              overflow: "hidden",
              background: "#E9E9E9",
            }}
          >
            <Box
              sx={{ overflowX: "auto", maxHeight: "65vh", overflowY: "auto" }}
            >
              <Table
                sx={{
                  tableLayout: "fixed",
                  width: TABLE_WIDTH,
                  minWidth: TABLE_WIDTH,
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  "& .MuiTableCell-root": {
                    boxSizing: "border-box",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    verticalAlign: "middle",
                  },
                  "& .property-address-cell": {
                    whiteSpace: "normal",
                    overflow: "visible",
                    textOverflow: "clip",
                    overflowWrap: "anywhere",
                    lineHeight: 1.25,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {/* Checkbox Column Header */}
                    <TableCell
                      sx={{
                        width: CHECKBOX_COL_WIDTH,
                        minWidth: CHECKBOX_COL_WIDTH,
                        position: "sticky",
                        fontWeight: 700,
                        fontFamily: "'proxima-regular', sans-serif",
                        fontSize: 14,
                        color: "#000000",
                        top: 0,
                        left: 0,
                        background: "#FFFFFF",
                        zIndex: 4,
                        border: "1px solid #D9D9D9",
                        padding: "2px 9px",
                        textAlign: "center",
                      }}
                    >
                      Select
                    </TableCell>

                    {/* Application ID */}
                    <TableCell
                      sx={{
                        width: ID_COL_WIDTH,
                        minWidth: ID_COL_WIDTH,
                        fontFamily: "'proxima-regular', sans-serif",
                        fontWeight: 700,
                        fontSize: 14,
                        color: "#000000",
                        position: "sticky",
                        top: 0,
                        left: CHECKBOX_COL_WIDTH,
                        background: "#FFFFFF",
                        zIndex: 3,
                        border: "1px solid #D9D9D9",
                        padding: "2px 9px",
                      }}
                    >
                      Application Number
                    </TableCell>

                    {/* Field Columns */}
                    {FIELD_COLUMNS.map((col) => (
                      <TableCell
                        key={col.label}
                        sx={{
                          width:
                            col.label === "Property Address"
                              ? PROPERTY_ADDRESS_COL_WIDTH
                              : FIELD_COL_WIDTH,

                          minWidth:
                            col.label === "Property Address"
                              ? PROPERTY_ADDRESS_COL_WIDTH
                              : FIELD_COL_WIDTH,
                          fontFamily: "'proxima-regular', sans-serif",
                          fontWeight: 700,
                          fontSize: 14,
                          color: "#000000",
                          position: "sticky",
                          top: 0,
                          background: "#FFFFFF",
                          zIndex: 2,
                          border: "1px solid #D9D9D9",
                          padding: "2px 9px",
                        }}
                      >
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applications.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={2 + FIELD_COLUMNS.length}
                        sx={{
                          border: "1px solid #D9D9D9",
                          textAlign: "center",
                          fontFamily: "'proxima-regular', sans-serif",
                          fontSize: 14,
                          color: "#666",
                          py: 3,
                        }}
                      >
                        No comparison data available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    applications.map((app, appIndex) => {
                      const backgroundColor = rowBg(appIndex);
                      const isRowHovered = hoveredRowIndex === appIndex;
                      const cellBackground = isRowHovered
                        ? HOVER_BACKGROUND
                        : backgroundColor;
                      const isSelected = selectedIds.has(
                        app.application_number,
                      );
                      const isLocked = lockedIds.has(app.application_number);

                      return (
                        <TableRow
                          key={app.application_number}
                          onMouseEnter={() => setHoveredRowIndex(appIndex)}
                          onMouseLeave={() => setHoveredRowIndex(null)}
                        >
                          {/* Checkbox Cell - Sticky Left */}
                          <TableCell
                            sx={{
                              width: CHECKBOX_COL_WIDTH,
                              minWidth: CHECKBOX_COL_WIDTH,
                              position: "sticky",
                              left: 0,
                              backgroundColor: cellBackground,
                              transition: "background-color 0.2s ease",
                              zIndex: 1,
                              border: "1px solid #D9D9D9",
                              padding: "2px 9px",
                              textAlign: "center",
                              fontFamily: "'proxima-regular', sans-serif",
                            }}
                          >
                            <Checkbox
                              checked={isSelected}
                              disabled={
                                isLocked ||
                                (!isSelected &&
                                  selectedIds.size >=
                                    Math.max(applications.length - 1, 0))
                              }
                              onChange={() =>
                                toggleSelect(app.application_number)
                              }
                              sx={{
                                color: "#000",
                                "&.Mui-checked": { color: "#077EE0" },
                                "&.Mui-checked.Mui-disabled": {
                                  color: "#90A4AE",
                                },
                                padding: "4px",
                                "& .MuiSvgIcon-root": {
                                  fontSize: 20,
                                  opacity: 0.9,
                                },
                              }}
                            />
                          </TableCell>

                          {/* Application ID - Sticky Left */}
                          <TableCell
                            sx={{
                              width: ID_COL_WIDTH,
                              minWidth: ID_COL_WIDTH,
                              fontFamily: "'proxima-regular', sans-serif",
                              fontSize: 14,
                              color: "#077EE0",
                              position: "sticky",
                              left: CHECKBOX_COL_WIDTH,
                              backgroundColor: cellBackground,
                              transition: "background-color 0.2s ease",
                              zIndex: 1,
                              border: "1px solid #D9D9D9",
                              padding: "2px 9px",
                            }}
                          >
                            {app.application_number}
                          </TableCell>

                          {/* Field Columns with hover effect */}
                          {FIELD_COLUMNS.map((col) => (
                            <TableCell
                              key={col.label}
                              className={
                                col.label === "Property Address"
                                  ? "property-address-cell"
                                  : undefined
                              }
                              sx={{
                                width:
                                  col.label === "Property Address"
                                    ? PROPERTY_ADDRESS_COL_WIDTH
                                    : FIELD_COL_WIDTH,
                                minWidth:
                                  col.label === "Property Address"
                                    ? PROPERTY_ADDRESS_COL_WIDTH
                                    : FIELD_COL_WIDTH,
                                fontFamily: "'proxima-regular', sans-serif",
                                fontSize: 14,
                                color: "#000000",
                                border: "1px solid #D9D9D9",
                                backgroundColor: cellBackground,
                                transition: "background-color 0.2s ease",
                                padding: "2px 9px",
                              }}
                            >
                              {col.render(app)}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}

        {/* Footer Action Area */}
        {!loading && !error && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              px: 2,
              pb: 3,
            }}
          >
            <Button
              variant="contained"
              disabled={!isAnySelected}
              onClick={handleMarkDuplicates}
              sx={{
                textTransform: "none",
                borderRadius: "20px",
                bgcolor: isAnySelected ? "#077EE0" : "#B0BEC5",
                padding: "8px 24px",
                fontFamily: "'proxima-regular', sans-serif",
                fontWeight: 600,
                fontSize: 14,
                "&:hover": {
                  bgcolor: isAnySelected ? "#0569C0" : "#B0BEC5",
                },
                transition: "all 0.2s ease",
              }}
            >
              Mark as Duplicate
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
